import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAppContext } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api';

// Shown only once Google Wallet credentials are configured on the server.
function GoogleWalletCard() {
  const [enabled, setEnabled] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('wallet/status').then(s => setEnabled(!!s.google)).catch(() => setEnabled(false));
  }, []);

  if (!enabled) return null;

  const addToWallet = async () => {
    setWorking(true);
    setError('');
    try {
      const { url } = await api.post('wallet/google');
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setWorking(false);
    }
  };

  return (
    <div className="mb-8 p-6 rounded-2xl border border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm flex-shrink-0">
          <SafeIcon name="CreditCard" size={22} />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Save your card to Google Wallet</h3>
          <p className="text-sm text-gray-500">Show a QR code to your profile right from your phone. It updates when you edit your profile.</p>
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
      </div>
      <button
        onClick={addToWallet}
        disabled={working}
        className="flex-shrink-0 inline-flex items-center justify-center gap-2 bg-black text-white rounded-full px-5 py-3 text-sm font-medium hover:bg-gray-800 disabled:opacity-60 transition-colors"
      >
        <SafeIcon name="CreditCard" size={16} />
        {working ? 'Preparing...' : 'Add to Google Wallet'}
      </button>
    </div>
  );
}

export default function Overview() {
  const { profile, devices } = useAppContext();
  const [upgrading, setUpgrading] = useState(false);

  if (!profile) return null;

  const isPro = profile.subscription_status === 'active' || profile.is_lifetime;

  const handleStripePayment = (paymentLink) => {
    setUpgrading(true);
    window.open(paymentLink, '_blank');
    // Reset upgrading state after a short delay
    setTimeout(() => setUpgrading(false), 2000);
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, {profile.first_name}!</h1>
          <p className="text-gray-500 mt-1">Manage your digital identity and devices.</p>
        </div>
        {!isPro && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-1 rounded-2xl shadow-lg shadow-indigo-100">
            <div className="bg-white px-6 py-3 rounded-[0.9rem] flex items-center gap-4">
              <div className="hidden sm:block">
                <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">Upgrade to Pro</p>
                <p className="text-sm font-bold text-gray-900">Unlock full potential</p>
              </div>
              <Button 
                size="sm" 
                onClick={() => handleStripePayment('https://buy.stripe.com/6oU7sMdaL1YOemoafG6Vq02')}
                disabled={upgrading}
              >
                {upgrading ? 'Connecting...' : 'Go Pro'}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-indigo-900">Active Devices</h3>
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600">
              <SafeIcon name="Smartphone" />
            </div>
          </div>
          <p className="text-3xl font-bold text-indigo-600">{devices.length}</p>
          <Link to="/dashboard/devices" className="text-sm text-indigo-600 hover:text-indigo-800 mt-2 inline-block font-medium">
            Manage devices &rarr;
          </Link>
        </div>

        <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-purple-900">Subscription</h3>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">
              <SafeIcon name="Star" />
            </div>
          </div>
          <p className="text-3xl font-bold text-purple-600 capitalize">
            {profile.is_lifetime ? 'Lifetime Pro' : (profile.subscription_status === 'free' ? 'Basic' : 'Pro')}
          </p>
          <p className="text-xs text-purple-600 mt-2 font-medium">
            {profile.is_lifetime ? 'No billing required' : (profile.subscription_tier === 'yearly' ? 'Yearly Billing' : profile.subscription_tier === 'monthly' ? 'Monthly Billing' : 'No active plan')}
          </p>
        </div>

        <div className="bg-green-50 rounded-2xl p-6 border border-green-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-green-900">Profile Views</h3>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
              <SafeIcon name="Eye" />
            </div>
          </div>
          <p className="text-3xl font-bold text-green-600">0</p>
          <p className="text-sm text-green-600 mt-2 font-medium">+0% this week</p>
        </div>
      </div>

      <GoogleWalletCard />

      {/* Pricing Section for Free Users */}
      {!isPro && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-6">
            <h2 className="text-xl font-bold text-gray-900">Choose your Pro Plan</h2>
            <div className="h-px flex-1 bg-gray-100"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 rounded-3xl border-2 border-gray-100 bg-white hover:border-indigo-200 transition-all group">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Monthly Subscription</h4>
              <p className="text-gray-500 text-sm mb-6">Perfect for short-term projects and events.</p>
              <div className="flex items-baseline gap-1 mb-8">
                <span className="text-4xl font-extrabold text-gray-900">$5</span>
                <span className="text-gray-400 font-medium">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['Custom branding', 'Advanced analytics', 'Unlimited links', 'Portfolio gallery'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <SafeIcon name="Check" className="text-green-500" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button 
                variant="secondary" 
                className="w-full py-4 text-lg" 
                onClick={() => handleStripePayment('https://buy.stripe.com/cNi6oI3Ab1YOguw3Ri6Vq04')}
                disabled={upgrading}
              >
                {upgrading ? 'Connecting to Stripe...' : 'Start Monthly'}
              </Button>
            </div>

            <div className="p-8 rounded-3xl border-2 border-indigo-600 bg-indigo-50/30 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase">
                Best Value
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Yearly Subscription</h4>
              <p className="text-gray-500 text-sm mb-6">Get all features plus a FREE physical device.</p>
              <div className="flex items-baseline gap-1 mb-8">
                <span className="text-4xl font-extrabold text-indigo-600">$60</span>
                <span className="text-gray-400 font-medium">/year</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-indigo-700 font-bold">
                  <SafeIcon name="Gift" />
                  FREE Physical Card or Keychain
                </li>
                {['Everything in Monthly', '2 months free', 'Priority support', 'Beta feature access'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <SafeIcon name="Check" className="text-green-500" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full py-4 text-lg shadow-lg shadow-indigo-200" 
                onClick={() => handleStripePayment('https://buy.stripe.com/6oU7sMdaL1YOemoafG6Vq02')}
                disabled={upgrading}
              >
                {upgrading ? 'Connecting to Stripe...' : 'Get Yearly + Free Card'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Public Card Preview */}
      <div className="border border-gray-200 rounded-2xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-semibold text-gray-900">Your Public Card</h3>
          <Link 
            to={`/p/${profile.username}`} 
            target="_blank" 
            className="text-indigo-600 text-sm font-medium flex items-center gap-1 hover:underline"
          >
            Preview <SafeIcon name="ExternalLink" className="text-xs" />
          </Link>
        </div>
        <div className="flex items-center gap-6 bg-gray-50 p-4 rounded-xl">
          <img 
            src={profile.avatar_url} 
            alt="Avatar" 
            className="w-16 h-16 rounded-full object-cover shadow-sm border border-white" 
          />
          <div>
            <p className="font-bold text-gray-900 text-lg">{profile.first_name} {profile.last_name}</p>
            <p className="text-sm text-gray-500">{profile.title} at {profile.company}</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
